import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import logoImage from '../assets/images/temple-logo.png';
import emblemLeft from '../assets/images/emblem-left.png';
import emblemRight from '../assets/images/emblem-right.png';
import AudioPlayer from './AudioPlayer';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavFixed, setIsNavFixed] = useState(false);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const headerTopRef = useRef(null);
  const headerNavRef = useRef(null);
  const wasNavFixedRef = useRef(false);

  useEffect(() => {
    let ticking = false;
    let rafId = null;

    const updateScrollState = () => {
      const currentScrollY = window.scrollY;
      const headerTop = headerTopRef.current;
      const headerNav = headerNavRef.current;
      
      if (!headerTop || !headerNav) {
        ticking = false;
        return;
      }
      
      // Get the actual height of the top header (use scrollHeight for accurate height)
      const headerTopHeight = headerTop.scrollHeight;
      
      // Determine scroll direction with a threshold to prevent jitter
      const scrollThreshold = 5;
      const scrollingDown = currentScrollY > lastScrollY + scrollThreshold;
      const scrollingUp = currentScrollY < lastScrollY - scrollThreshold;
      
      // Make nav fixed when scrolled past the top header
      // Get the position where nav should stick (top header height)
      const navShouldBeFixed = currentScrollY >= headerTopHeight;
      setIsNavFixed(navShouldBeFixed);
      
      // Add padding to body when nav becomes fixed to prevent content jump
      if (navShouldBeFixed && !wasNavFixedRef.current) {
        const navHeight = headerNav.offsetHeight;
        document.body.style.paddingTop = `${navHeight}px`;
        wasNavFixedRef.current = true;
      } else if (!navShouldBeFixed && wasNavFixedRef.current) {
        document.body.style.paddingTop = '0';
        wasNavFixedRef.current = false;
      }
      
      // Hide top header only when:
      // 1. Scrolling down AND
      // 2. Scroll position has passed the top header height
      if (scrollingDown && currentScrollY >= headerTopHeight) {
        setIsScrolled(true);
      } 
      // Show top header when:
      // 1. Scrolling up OR
      // 2. At the top of the page
      else if (scrollingUp || currentScrollY <= headerTopHeight) {
        setIsScrolled(false);
      }
      
      setLastScrollY(currentScrollY);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    // Check initial scroll position on mount (with small delay to ensure DOM is ready)
    setTimeout(() => {
      updateScrollState();
    }, 0);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      // Cleanup: remove padding when component unmounts
      document.body.style.paddingTop = '0';
    };
  }, [lastScrollY]);

  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Wait a bit to ensure DOM is fully rendered, then scroll to absolute bottom
    setTimeout(() => {
      const scrollHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      
      window.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  return (
    <header className="header">
      <div ref={headerTopRef} className={`header-top ${isScrolled ? 'header-top-hidden' : ''}`}>
        <div className="container">
          <div className="header-top-content">
            <div className="logo logo-left">
              <img 
                src={logoImage} 
                alt="GPR Grande Sri Lakshmi Venkateswara Temple Logo"
              />
            </div>

            <div className="temple-name-center">
              <img 
                src={emblemLeft} 
                alt="Temple Emblem"
                className="temple-emblem-left"
              />
              <h1 className="temple-name-telugu">
                <span className="temple-name-line1">GPR GRANDE</span>
                <span className="temple-name-line2">Sri Lakshmi Venkateswara Temple</span>
              </h1>
              <img 
                src={emblemRight} 
                alt="Temple Emblem"
                className="temple-emblem-right"
              />
            </div>

            <div className="temple-image-right">
              <img 
                src={logoImage} 
                alt="GPR Grande Sri Lakshmi Venkateswara Temple Logo"
                className="temple-gopuram-img"
              />
            </div>
          </div>
        </div>
      </div>
      
      <div ref={headerNavRef} className={`header-nav ${isNavFixed ? 'header-nav-fixed' : ''}`}>
        <div className="container">
          <nav className={`nav ${isMenuExpanded ? 'nav-menu-expanded' : ''}`}>
            <ul className={`nav-main ${isMenuExpanded ? 'nav-expanded' : ''}`}>
              <li><a href="#home"><i className="fas fa-home"></i> <span>Home</span></a></li>
              <li><a href="#services"><i className="fas fa-star"></i> <span>Services</span></a></li>
              <li><a href="#schedule"><i className="fas fa-clock"></i> <span>Schedule</span></a></li>
              <li className="nav-item-mobile-hidden"><a href="#about-temple"><i className="fas fa-info-circle"></i> <span>About Temple</span></a></li>
              <li className="nav-item-mobile-hidden"><a href="#gallery"><i className="fas fa-images"></i> <span>Gallery</span></a></li>
              <li className="nav-item-mobile-hidden"><a href="#" onClick={handleContactClick}><i className="fas fa-map-marker-alt"></i> <span>Contact</span></a></li>
            </ul>
            <button 
              className="nav-more-btn"
              onClick={() => setIsMenuExpanded(!isMenuExpanded)}
              aria-label={isMenuExpanded ? "Show less" : "Show more"}
              aria-expanded={isMenuExpanded}
            >
              <i className={isMenuExpanded ? "fas fa-times" : "fas fa-ellipsis-v"}></i>
            </button>
            <div className="header-audio-player">
              <AudioPlayer />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;